<template>
    <App title="Update Financier">
        <div class="w-100 position-relative">
            <div class="update-layout">

                <!-- ── Hero ──────────────────────────────────────────────────── -->
                <div class="update-hero">
                    <div class="update-avatar">
                        <i class="fas fa-hand-holding-usd"></i>
                    </div>
                    <div class="update-hero-info">
                        <h2 class="update-name">{{ financier.title }}</h2>
                        <span class="update-role-badge">Financier</span>
                    </div>
                    <div class="update-hero-actions">
                        <a :href="route('financier.page.group')" class="btn btn-sm btn-outline-secondary">
                            <i class="fas fa-arrow-left me-1"></i> Back
                        </a>
                    </div>
                </div>

                <!-- ── Form card ──────────────────────────────────────────────── -->
                <div class="update-card">
                    <div class="update-card-header">
                        <i class="fas fa-user-tie me-2 text-muted"></i>
                        <span>Financier Details</span>
                    </div>

                    <Form ref="form" @submit="onSubmit">
                        <div class="update-form-grid">

                            <div class="update-field">
                                <label for="title" class="update-label">Title</label>
                                <InputField id="title" v-model="title" name="title" rules="required" label="Title" placeholder="Input title.." />
                            </div>

                            <div class="update-field">
                                <label class="update-label">Webhook</label>
                                <InputField id="webhook" type="webhook" v-model="webhook" name="webhook" rules="required" label="Webhook" placeholder="Input Webhook URL.." />
                            </div>

                            <div class="update-field">
                                <label class="update-label">Secret</label>
                                <InputField id="secret" type="secret" v-model="secret" name="secret" rules="required" label="Secret" placeholder="Input Secret.." />
                            </div>

                            <div class="update-field">
                                <label for="email" class="update-label">Email</label>
                                <InputField id="email" type="email" v-model="email" name="email" rules="required|email" label="Email" placeholder="Input email .." />
                            </div>

                            <div class="update-field">
                                <label for="phone" class="update-label">Phone</label>
                                <PhoneField id="phone" v-model="phone" name="phone" rules="required|phone" label="Phone Number" placeholder="+234 800 000 0000" />
                            </div>

                            <div class="update-field">
                                <label for="percentage" class="update-label">Percentage (%)</label>
                                <InputField id="percentage" v-model="percentage" name="percentage" rules="required" label="Percentage" placeholder="e.g. 10" />
                            </div>

                        </div>

                        <!-- ── Account section ─────────────────────────────────── -->
                        <div class="update-section-divider">
                            <i class="fas fa-university me-2 text-muted"></i>
                            <span>Account Information</span>
                        </div>

                        <div class="update-form-grid">

                            <div class="update-field">
                                <label for="bank" class="update-label">Bank Name</label>
                                <SelectField id="bank" ref="bankField" class="bank-name-select" v-model="account.bank" name="bank" rules="required" label="Bank Name" :options="banks_options" />
                            </div>

                            <div class="update-field">
                                <label for="number" class="update-label">Account Number</label>
                                <InputField :disabled="account.bank === ''" id="number" v-model="account.number" name="number" rules="max:10|numeric" label="Account Number" placeholder="10-digit account number" @blur="isAccountValid" />
                                <span class="update-hint update-hint--muted" v-if="validatingAccount">
                                    <i class="fas fa-spinner fa-spin me-1"></i> Validating account…
                                </span>
                                <span class="update-hint update-hint--error" v-if="invalid_acctno_msg && !validatingAccount">
                                    <i class="fas fa-exclamation-circle me-1"></i>{{ invalid_acctno_msg }}
                                </span>
                                <span class="update-hint update-hint--success" v-if="account.name && !validatingAccount">
                                    <i class="fas fa-check-circle me-1"></i>{{ account.name }}
                                </span>
                            </div>

                        </div>

                        <input type="hidden" id="index" name="index" v-model="account.index" />

                        <!-- ── Actions ─────────────────────────────────────────── -->
                        <div class="update-form-actions">
                            <button type="button" class="btn btn-outline-danger" @click="goBack">
                                <i class="fas fa-times me-1"></i> Cancel
                            </button>
                            <Button id="action" type="submit" :label="labels" :loading="loading" :done="done" class="btn btn-primary" :disabled="checkInput" />
                        </div>
                    </Form>
                </div>

            </div>
        </div>
    </App>
</template>

<script lang="ts">
import banks from '@/json/banks.json' // Your JSON file
import $ from 'jquery'
import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import PhoneField from '@/vue/components/PhoneField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import TextareaField from '@/vue/components/TextareaField.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { Options as Select2Options } from 'select2'
import Swal from 'sweetalert2'
import { Component, Prop, Ref, Vue, Watch, toNative } from 'vue-facing-decorator'


@Component({
    components: {
        App,
        Form,
        Alert,
        InputField,
        PhoneField,
        Button,
        Block,
        SelectField,
        TextareaField,
    },
})
class Create extends Vue {
    message = ''
    loading = false
    done = false

    title = ''
    webhook = ''
    secret = ''
    email = ''
    phone = ''
    percentage = ''
    validatingAccount = false
    account = {
        number: '',
        index: '',
        bank: '',
        name: '',

    }
    invalid_acctno_msg = ''

    @Prop({ type: Object })
    readonly types!: Record<string, string>[]
    @Prop({ type: Object })
    readonly financier!: any

    @Ref('form')
    readonly form!: InstanceType<typeof Form>

    @Ref('bankField') bankField!: any

    get banks_options(): Select2Options {
        return {
            ajax: null,
            placeholder: 'Select Bank',
            minimumResultsForSearch: 2,
            data: banks.map((bank, i) => ({
                id: bank.name, // just the code
                text: bank.name,
            })),
        }
    }

    get labels(): ButtonLabel {
        return {
            normal: 'Update Financier',
            loading: 'Updating...',
            done: 'Updated',
        }
    }

    get checkInput(): boolean {
        return [this.title, this.percentage, this.email, this.phone, this.account.bank, this.account.number, this.account.index]
            .some(field => field.trim() === '')
    }


    showToast(message: string, type: 'success' | 'error') {

        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: type,
            title: message,
            showConfirmButton: false,
            timer: 5000,
            timerProgressBar: true,
            didOpen: (toast) => {
                // Pause the timer when the mouse hovers over the toast
                toast.addEventListener('mouseenter', Swal.stopTimer)
                // Resume the timer when the mouse leaves
                toast.addEventListener('mouseleave', Swal.resumeTimer)
            }
        })
    }

    @Watch('account.bank')
    onFetchAccountName(value: string) {
        if (typeof value === 'undefined') {
            this.account.index = ''
            return
        }

        if (value.length < 10) {
            this.account.index = ''
            return
        }

        this.message = ''

        if (value) {
            const selectedBank = banks.find(b => b.name === value)
            if (selectedBank) {
                this.account.index = selectedBank.index

            }
        }
        else {
            this.account.index = ''

        }
    }


    created() {
        // Set existing data
        this.title = this.financier.title
        this.webhook = this.financier.webhook || ''
        this.secret = this.financier.secret || ''
        this.email = this.financier.email
        this.phone = this.financier.phone
        this.percentage = this.financier.percentage
        if (this.financier.account) {
            this.account.bank = this.financier.account.bank
            this.account.number = this.financier.account.number
            this.account.index = this.financier.account.index
            this.account.name = this.financier.account.name
        }
    }

    mounted() {
        if (this.financier.account && this.financier.account.bank) {
            this.$nextTick(() => {
                setTimeout(() => {
                    const selectElement = this.bankField?.$el?.querySelector('select')
                    if (selectElement) {
                        $(selectElement).val(this.account.bank).trigger('change')
                    }
                }, 200)
            })
        }
    }


    async isAccountValid() {
        if (!this.account.number || !this.account.index) return

        this.validatingAccount = true
    }

    async onSubmit(payload) {
        this.loading = true
        this.message = ''
        payload.index = this.account.index



        this.$http
            .put(this.route('financier.ajax.update', { financier: this.financier.slug }), payload)
            .then((response) => {
                this.done = true
                this.message = response.data.message || 'Financier record updated successfully!'
                Swal.fire({
                    title: 'Success!',
                    text: this.message,
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    this.router.visit(this.route('financier.page.group'))
                    this.message = ''
                    Swal.close()

                })


            })
            .catch((e) => {
                this.message = e.response?.data?.message || 'An error occurred.Please contact support'
                Swal.fire({
                    title: 'Error!',
                    text: this.message,
                    icon: 'error',
                    confirmButtonText: 'OK',
                })
                //console.error(e)
            })
            .finally(() => (this.loading = false))
    }

    goBack() {
        window.history.back()
    }
}

export default toNative(Create)
</script>

<style scoped lang="scss">
.update-layout {
    max-width: 760px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.update-hero {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    background: #fff;
    border-radius: 12px;
    padding: 1.5rem 1.75rem;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    flex-wrap: wrap;
}

.update-avatar {
    flex-shrink: 0;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1e3a2f, #3a7d52);
    color: #fff;
    font-size: 1.35rem;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(30, 58, 47, 0.25);
}

.update-hero-info {
    flex: 1;
    min-width: 0;
}

.update-name {
    font-size: 1.15rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.3rem;
    line-height: 1.2;
}

.update-role-badge {
    display: inline-block;
    padding: 0.2rem 0.7rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: #dcfce7;
    color: #15803d;
}

.update-hero-actions {
    flex-shrink: 0;
    margin-left: auto;
}

.update-card {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.update-card-header {
    padding: 0.875rem 1.75rem;
    border-bottom: 1px solid #f1f3f5;
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
    background: #f9fafb;
    display: flex;
    align-items: center;
}

.update-section-divider {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.75rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    background: #f9fafb;
    border-top: 1px solid #f1f3f5;
    border-bottom: 1px solid #f1f3f5;
}

.update-form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    padding: 1.5rem 1.75rem;

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
}

.update-field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    &--full {
        grid-column: 1 / -1;
    }
}

.update-label {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin-bottom: 0;
}

.update-hint {
    font-size: 0.8rem;
    margin-top: 0.25rem;

    &--muted {
        color: #6b7280;
    }

    &--error {
        color: #dc2626;
    }

    &--success {
        color: #16a34a;
    }
}

.update-form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.625rem;
    padding: 1rem 1.75rem;
    border-top: 1px solid #f1f3f5;
    background: #f9fafb;
}
</style>
